<template>
  <el-tabs v-model="activeName">
    <el-tab-pane label="Main info" name="commonInfo">
      <div class="row">
        <div class="col-md-6">
          <template v-if="isConnectionVisible">
            <IllnessConnection
              v-if="entityData.type === 'illness'"
              :entity="entityData.entity"/>
            <template v-else>
              <ProcedureConnections v-if="entityData.entity.id"
                                    :entity="entityData.entity"
                                    :entityType="entityData.type"
                                    :entitiesString="entityData.entitiesString"
                                    @get-procedure-event="getEntityEvent"/>
            </template>
          </template>
          <EntityNameInfo v-if="domains && domains.length" :entity="entityData.entity"/>
          <EntityFlags :entityData="entityData"/>
        </div>
        <div class="col-md-6">
          <EntityDetails v-if="entityData.details" :details="entityData.details"/>
          <CityCountry :entity="entityData.entity"
                       v-if="entityData.type === 'city'"/>
        </div>
      </div>
    </el-tab-pane>
    <template v-if="domains && domains.length">
      <el-tab-pane label="Name forms" name="nameForms">
        <EntityNameForms :entityData="entityData"/>
      </el-tab-pane>
      <template v-if="!isNewEntity">
        <el-tab-pane label="Texts" name="texts">
          <EntityTexts :data="entityData.texts"/>
        </el-tab-pane>
        <el-tab-pane label="Countries texts" name="connectionWithCountries"
                     v-if="entityData.type !== 'city' && entityData.type !== 'country'">
          <ConnectionWithCountries :entitiesString="entityData.entitiesString"/>
        </el-tab-pane>
        <el-tab-pane label="Photo-match tags" name="photoMatchTags"
                     v-if="entityData.type === 'operation'">
          <OperationPhotoMatchTags :operationId="entityData.entityId"/>
        </el-tab-pane>
      </template>
    </template>
    <div class="row">
      <div class="col-12">
        <el-alert v-if="entityData.type === 'city'"
                  title="New cities and geographic changes are verified before saving. Countries, districts and duplicate cities are not accepted."
                  type="info" :closable="false" show-icon class="mb-3"/>
        <el-alert v-if="cityValidationMessage" :title="cityValidationMessage"
                  type="error" :closable="false" show-icon class="mb-3"/>
        <base-button type="primary"
                     v-if="activeName !== 'texts' && activeName !== 'connectionWithCountries' && activeName !== 'photoMatchTags'"
                     :loading="saving"
                     :disabled="saving"
                     @click="save()">{{isNewEntity ? 'Create' : 'Update'}} {{ entityData.type }}</base-button>
      </div>
    </div>
  </el-tabs>
</template>

<script>
import ProcedureConnections from '@/components/Entities/ProcedureConnections';
import IllnessConnection from '@/components/Entities/IllnessConnection';
import EntityNameInfo from '@/components/Entities/EntityNameInfo';
import EntityFlags from '@/components/Entities/EntityFlags';
import EntityDetails from '@/components/Entities/EntityDetails';
import CityCountry from '@/components/Entities/CityCountry';
import EntityNameForms from '@/components/Entities/EntityNameForms';
import EntityTexts from '@/components/Entities/EntityTexts';
import ConnectionWithCountries from '@/components/Entities/ConnectionWithCountries';
import OperationPhotoMatchTags from '@/components/Entities/OperationPhotoMatchTags';
import { mapState, mapActions } from 'vuex';

export default {
  props: ['entityData'],
  components: {
    EntityNameInfo,
    EntityDetails,
    EntityFlags,
    ProcedureConnections,
    IllnessConnection,
    EntityNameForms,
    EntityTexts,
    ConnectionWithCountries,
    CityCountry,
    OperationPhotoMatchTags,
  },
  data: () => ({
    activeName: 'commonInfo',
    saving: false,
    cityValidationMessage: '',
  }),
  computed: {
    ...mapState('cmsApiCatalogs', ['domains']),
    isConnectionVisible() {
      return this.entityData.type !== 'illness-kind' && this.entityData.type !== 'city' && this.entityData.type !== 'country';
    },
    isNewEntity() {
      return this.entityData.entityId === 'new';
    },
  },
  created() {
    this.getIllnessKinds({ perPage: 1500 });
    this.getEntityEvent();
    this.getDomains();
  },
  methods: {
    ...mapActions('cmsApiCatalogs', ['getIllnessKinds', 'getEntity', 'updateEntityDetails', 'getDomains', 'saveEntity']),
    getEntityEvent() {
      if (!this.isNewEntity) {
        this.$store.dispatch('setLoader', true);
        return this.getEntity({
          entityId: this.entityData.entityId,
          type: this.entityData.type,
          entitiesString: this.entityData.entitiesString,
        })
          .finally(() => this.$store.dispatch('setLoader', false));
      }
      return null;
    },
    async save() {
      if (this.saving) return;
      this.cityValidationMessage = '';

      if (this.entityData.type === 'country') {
        const entity = this.entityData.entity;
        if (!(/^[a-zA-Z'-\s]+$/.test(entity.code)) || entity.code.length < 1) {
          alert('Iso code has to be min 2 symbols and latin');
          return;
        } else if (!entity.country_group) {
          alert('Select country group');
          return;
        }
      }
      if (this.entityData.type === 'illness' && !this.entityData.entity.illness_kind) {
        alert('Set specialty');
        return;
      }
      if (this.entityData.type === 'city' && !this.entityData.entity.country) {
        alert('Set country');
        return;
      }

      this.saving = true;
      this.$store.dispatch('setLoader', true);

      const wasNewEntity = this.isNewEntity;
      let createdEntityId = null;

      try {
        const entityResponse = await this.saveEntity({
          data: this.entityData.entity,
          entitiesString: this.entityData.entitiesString,
        });

        createdEntityId = entityResponse?.data?.id;

        if (!createdEntityId && this.entityData.type !== 'country') {
          throw new Error('Entity was not created');
        }

        if (wasNewEntity) {
          const redirectEntityId = createdEntityId
            || (this.entityData.type === 'country' ? this.entityData.entity.code : null);

          if (redirectEntityId) {
            if (createdEntityId) {
              this.entityData.entity.id = createdEntityId;
            }

            await this.$router.push({ params: { entityId: String(redirectEntityId) } });
          }
        }

        if (this.entityData.details) {
          const detailsEntityId = this.entityData.type === 'country'
            ? this.entityData.entity.code
            : createdEntityId;

          this.entityData.details = this.entityData.details.map((detail) => {
            const detailObj = { ...detail };

            if (this.entityData.type === 'country') {
              detailObj.iso_code = this.entityData.entity.code;
            } else {
              detailObj[`${this.entityData.type.replace('-', '_')}_id`] = detailsEntityId;
            }

            return detailObj;
          });

          try {
            await this.updateEntityDetails({
              data: this.entityData.details,
              entityId: detailsEntityId,
              entitiesString: this.entityData.entitiesString,
            });
          } catch (detailsError) {
            const detailsMessage = wasNewEntity && createdEntityId
              ? `${this.entityData.type} created (ID ${createdEntityId}), but details failed to save`
              : 'Details failed to save';

            this.$notify({ type: 'warning', title: detailsMessage });
            return;
          }
        }

        this.$notify({ type: 'success', title: 'Saved' });
      } catch (e) {
        if (this.entityData.type === 'city' && typeof e?.response?.data?.error === 'string' && e.response.data.error.startsWith('city_')) {
          this.cityValidationMessage = e.response.data.message || 'City verification failed. Nothing was saved.';
          this.$notify({ type: 'warning', title: this.cityValidationMessage });
          return;
        }
        if (wasNewEntity && createdEntityId) {
          this.$notify({
            type: 'warning',
            title: `${this.entityData.type} created (ID ${createdEntityId}), but save did not complete`,
          });
          return;
        }

        this.$notify({ type: 'warning', title: 'Save Failed' });
      } finally {
        this.saving = false;
        this.$store.dispatch('setLoader', false);
      }
    },
  }
}
</script>
